import React, { useEffect, useRef } from 'react';
import { PROJECTS } from '../../data/portfolio';
import ProjectMark from './ProjectMark';

interface DrawerProps {
  projectId: string | null;
  onClose: () => void;
}

function archSVG(steps: string[]): string {
  const w = 150;
  const gap = 34;
  const h = 42;
  const total = steps.length * w + (steps.length - 1) * gap;
  return `<svg viewBox="0 0 ${total} ${h + 16}" width="${total}" height="${h + 16}" role="img" aria-label="Architecture: ${steps.join(' then ')}">
    ${steps
      .map((s, i) => {
        const x = i * (w + gap);
        const arrow =
          i < steps.length - 1
            ? `<line x1="${x + w}" y1="${h / 2 + 8}" x2="${x + w + gap - 7}" y2="${h / 2 + 8}" stroke="#A8E831" stroke-width="1"/>
           <path d="M${x + w + gap - 7},${h / 2 + 8} l-6,-3.5 v7 z" fill="#A8E831"/>`
            : '';
        return `<rect x="${x}" y="8" width="${w}" height="${h}" rx="3" fill="none" stroke="#A8E831" stroke-opacity=".55" stroke-width="1"/>
        <text x="${x + w / 2}" y="${h / 2 + 13}" text-anchor="middle" fill="#E6EDE2" font-family="JetBrains Mono,monospace" font-size="12">${s}</text>${arrow}`;
      })
      .join('')}
  </svg>`;
}

const Drawer: React.FC<DrawerProps> = ({ projectId, onClose }) => {
  const drawerRef = useRef<HTMLElement>(null);
  const project = PROJECTS.find((p) => p.id === projectId) || null;
  const open = !!project;

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    drawerRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <>
      <div className={`scrim${open ? ' on' : ''}`} onClick={onClose} aria-hidden={!open} />
      <aside
        ref={drawerRef}
        className={`drawer${open ? ' on' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Project detail"
        tabIndex={-1}
      >
        {project && (
          <div className="inner">
            <button type="button" className="closebtn" onClick={onClose}>
              close ×
            </button>
            <ProjectMark
              name={project.name}
              id={project.id}
              logo={project.logo}
              size="lg"
              className="drawer-mark"
            />
            <h2>{project.name}</h2>
            <p className="tagline">{project.summary}</p>
            <div className="metrics">
              {project.metrics.map(([v, k]) => (
                <div key={k} className="metric">
                  <div className="v">{v}</div>
                  <div className="k">{k}</div>
                </div>
              ))}
            </div>
            <div className="body">
              {project.body.map((b) => (
                <p key={b.slice(0, 40)}>{b}</p>
              ))}
            </div>
            <div className="arch">
              <div className="label" style={{ marginBottom: 16 }}>
                architecture
              </div>
              <div dangerouslySetInnerHTML={{ __html: archSVG(project.arch) }} />
            </div>
            <div className="tags">
              {project.tags.map((t) => (
                <span key={t}>{t}</span>
              ))}
            </div>
            <div className="acts">
              {project.links.map(([t, h]) => (
                <a key={t} href={h} target="_blank" rel="noopener noreferrer">
                  {t} ↗
                </a>
              ))}
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default Drawer;
